import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import type { LeDataSessionSummary } from '~/types/leData';

import { deriveSessionRowTitle } from './chat/SessionHistoryPanel';

/** The list of past conversations scoped to one Lens or one card, shared by the shell's own Lens
 *  chat drawer and the card-scoped Ask pane's "History" toggle. */
export function LensChatHistory({
  sessions,
  title = 'Lens conversations',
  emptyText = 'No previous Lens conversations yet.',
  selectedSessionId,
  isLoading,
  onNewChat,
  onOpenSession,
}: {
  sessions: Array<LeDataSessionSummary>;
  title?: string;
  emptyText?: string;
  selectedSessionId: string | null;
  isLoading: boolean;
  onNewChat: () => void;
  onOpenSession: (sessionId: string) => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        maxWidth: '100%',
        px: 1.5,
        py: 2,
      }}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography fontWeight={650} variant="subtitle2">
          {title}
        </Typography>
        <IconButton aria-label="New chat" onClick={onNewChat} size="small" title="New chat">
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : sessions.length === 0 ? (
        <Box sx={{ pt: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ overflowY: 'auto', minWidth: 0, maxWidth: '100%' }}>
          {sessions.map((session) => (
            <ListItemButton
              key={session.sessionId}
              onClick={() => onOpenSession(session.sessionId)}
              selected={session.sessionId === selectedSessionId}
              sx={{
                alignItems: 'flex-start',
                borderRadius: 1,
                minWidth: 0,
                maxWidth: '100%',
                mb: 0.5,
                px: 1,
                py: 0.75,
              }}
            >
              <ListItemText
                primary={deriveSessionRowTitle(session)}
                primaryTypographyProps={{ noWrap: true, variant: 'body2', title: deriveSessionRowTitle(session) }}
                sx={{ minWidth: 0 }}
                secondary={new Date(session.updatedAt).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                })}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
}

export default LensChatHistory;
